import { useSetAtom } from "jotai";
import { useParams } from "react-router-dom";
import { useAsync } from "react-use";
import { userTypeAtom } from "../../auth/state";
import { LoadingScreen } from "../../components/LoadingScreen";
import { useIsValidProviderAndPatient } from "../../hooks/useIsValidProviderAndPatient";
import { useGracefullyUpdateStatsigUserCallback } from "../../statsig/useGracefullyUpdateStatsigUserCallback";
import { UserType } from "../../types";
import { useGracefullyAuthorizeCurrentUserCallback } from "../auth/useGracefullyAuthorizeCurrentUserCallback";
import { useGracefullyLoadVisitorSegmentCallback } from "../segment/useGracefullyLoadVisitorSegmentCallback";
import { useInitializeVideoSDKAdapter } from "../../video-sdk/actions/useLifecycleActions";
import { useIsZoomEnabled } from "../../hooks/useIsZoomEnabled";
import { ErrorPage } from "../../error/ErrorPage";

/**
 * Handles any necessary pre-rendering tasks for the visitor route e.g. updating
 * the Statsig user. These are tasks that need to be done after the visitor user
 * info is available (i.e. route params) and before the visitor route content is
 * rendered.
 */
export function WithVisitorRoutePreRender({
  children,
}: {
  children: React.ReactNode;
}) {
  const setUserType = useSetAtom(userTypeAtom);
  const gracefullyUpdateStatsigUser = useGracefullyUpdateStatsigUserCallback();
  const gracefullyLoadVisitorSegment =
    useGracefullyLoadVisitorSegmentCallback();

  const { patientShortId, providerShortId } = useParams<{
    patientShortId: string;
    providerShortId: string;
  }>();
  const { loading: isValidProviderAndPatientLoading } =
    useIsValidProviderAndPatient(providerShortId!, patientShortId!);

  const gracefullyAuthorizeCurrentUser =
    useGracefullyAuthorizeCurrentUserCallback();
  const initializeVideoSDKAdapter = useInitializeVideoSDKAdapter();
  const [, checkZoomIsEnabled] = useIsZoomEnabled();

  const { loading, error } = useAsync(
    async function loadBeforeRender() {
      setUserType(UserType.CLIENT);
      await Promise.allSettled([
        gracefullyLoadVisitorSegment({
          providerShortId,
          patientShortId,
        }),
        gracefullyUpdateStatsigUser({
          userID: patientShortId,
          customIDs: {
            providerShortID: providerShortId,
          },
          custom: {
            userType: UserType.CLIENT,
          },
        }),
      ]);
      // Logic that depends on the Statsig user update 👇

      // Video SDK initialization error should be blocking
      await initializeVideoSDKAdapter(checkZoomIsEnabled() ? "zoom" : "twilio");

      await gracefullyAuthorizeCurrentUser({
        variables: { patientShortId: patientShortId ?? "" },
      });
    },
    [
      gracefullyUpdateStatsigUser,
      gracefullyAuthorizeCurrentUser,
      patientShortId,
    ],
  );

  if (loading || isValidProviderAndPatientLoading) {
    return <LoadingScreen />;
  }

  if (error) {
    return <ErrorPage error={error} />;
  }

  return children;
}
